// @flow

import { useMemo } from 'react';
import { useQuery } from 'react-query';
import isEmpty from 'lodash/isEmpty';

import Rd356010FormService from '../../services/rd356010FormService';
import type { Rd356010Form } from '../../containers/RentForecast/Customize356010Modal/types';

// The 3560-10 values the report would print for one property and as-of date,
// loaded from Intacct by the backend. cacheTime is 0 because nothing about
// this form is kept: closing the modal discards it, and reopening loads fresh.
// Every fetch resets the user's edits, so only an explicit Refresh may refetch:
// no refetch on window focus or on network reconnect.
const useRd356010FormQuery = ({
  organizationId,
  propertyId,
  asOfDate,
  options = {},
}: {
  organizationId: ?string,
  propertyId: ?string,
  asOfDate: ?string,
  options?: Object,
}) => {
  const service = useMemo(() => new Rd356010FormService(), []);

  return useQuery<Rd356010Form>(
    ['rd-3560-10-form', organizationId, propertyId, asOfDate],
    () => {
      if (!organizationId || !propertyId || !asOfDate) {
        throw new Error('organizationId, propertyId and asOfDate are required');
      }
      return service.getForm(organizationId, propertyId, asOfDate);
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      useErrorBoundary: false,
      retry: false,
      cacheTime: 0,
      enabled:
        !isEmpty(organizationId ?? '') &&
        !isEmpty(propertyId ?? '') &&
        !isEmpty(asOfDate ?? ''),
      ...options,
    },
  );
};

export default useRd356010FormQuery;
